import classNames from 'clsx';
import { isEmpty } from 'lodash';
import { DropzoneArea } from 'material-ui-dropzone';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Translation from '@swf/components/Translation/Translation';
import Close from '@swf/core/assets/icons/close.svg';
import Success from '@swf/core/assets/icons/success.svg';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';

import type { HelpSection } from '../../../types/Cashier';
import { Error } from '../../../types/paymentVerification';
import ButtonGroup from '../../ButtonGroup/ButtonGroup';
import CTAButton from '../../CTAButton/CTAButton';
import Container from '../../Container/Container';
import DepositPaymentVerificationPrompt from '../DepositPaymentVerificationPrompt/DepositPaymentVerificationPrompt';
import HelpSectionContent from '../HelpSectionContent/HelpSectionContent';
import UploadError from '../UploadError/UploadError';

import {
  TXTWCL_Drag_and_drop_a_file_here_or_c_ELL,
  TXTWCL_I_canXt_upload_my_file,
  TXTWCL_Verify_your_payment_method,
  TXTWCL_Why_verify_your_payment_method_ELL
} from './DepositPaymentVerification.messages.json';
import {
  active,
  chipDeleteIcon,
  chipLabel,
  chipPrimaryIcon,
  droparea,
  gridContainer,
  hidden,
  icon,
  imageContainer,
  invalid,
  preview,
  text,
  textContainer
} from './DepositPaymentVerification.scss';
import DocumentRequirements from './components/DocumentRequirements/DocumentRequirements';
import {
  defaultMessages,
  translationMap
} from './components/DocumentRequirements/utils/translationUtils';
import DropzoneContent from './components/DropzoneContent/DropzoneContent';
import SubmitButton from './components/SubmitButton/SubmitButton';

const MAX_FILE_SIZE = 10000000;
const MAX_FILES = 5;

interface DropzoneAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

const DepositPaymentVerification: React.FC = () => {
  const { jumpBack } = useWizardJump();
  const { formatMessage } = useIntl();
  const {
    values: {
      deposit: { type }
    }
  } = useWizardContext();
  const { subtitle } = translationMap[type] || defaultMessages;

  const [error, setError] = useState<Error>();
  const [numberOfAddedFiles, setNumberOfAddedFiles] = useState<number>(0);
  const [showDropZone, setShowDropZone] = useState<boolean>(true);
  const [showPaymentVerificationPrompt, setShowPaymentVerificationPrompt] =
    useState<boolean>(false);

  useEffect(() => setShowDropZone(numberOfAddedFiles < MAX_FILES), [numberOfAddedFiles]);

  const helpSection: HelpSection = {
    title: TXTWCL_Why_verify_your_payment_method_ELL,
    body: <HelpSectionContent />
  };

  const goBack = () => jumpBack('#deposit.deposit-payment-verification-menu');

  const onDelete = () => {
    setNumberOfAddedFiles(numberOfAddedFiles - 1);
    setError(undefined);
  };

  const onDrop = (acceptedFiles: File[], rejectedFiles: File[]) => {
    const totalAcceptedFiles = numberOfAddedFiles + acceptedFiles.length;
    const totalAddedFiles = totalAcceptedFiles + rejectedFiles.length;

    if (totalAddedFiles > MAX_FILES) {
      setError(Error.MaxFiles);
      return;
    }

    if (!isEmpty(rejectedFiles)) {
      if (rejectedFiles.some((file) => file.size > MAX_FILE_SIZE)) {
        setError(Error.Size);
      } else if (rejectedFiles.some((file) => !/(jpg|jpeg|png|pdf)$/.test(file.type))) {
        setError(Error.Type);
      } else {
        setError(Error.Default);
      }
    } else {
      setError(undefined);
    }

    if (!isEmpty(acceptedFiles)) {
      setNumberOfAddedFiles(totalAcceptedFiles);
    }
  };

  return (
    <Container
      headerProps={{
        title: <Translation {...TXTWCL_Verify_your_payment_method} />,
        buttons: { back: true, onBackClick: goBack }
      }}
      page={DepositPaymentVerification}
      pageTopTitle={<Translation {...TXTWCL_Verify_your_payment_method} />}
      pageTopSubtitle={<Translation {...subtitle} />}
      helpSupportResolver
      showRegulatoryInformation
      showFooter
      helpSection={helpSection}
    >
      <DocumentRequirements />
      <DropzoneArea
        acceptedFiles={['image/jpg', 'image/jpeg', 'image/png', 'application/pdf']}
        classes={{
          active,
          icon,
          invalid,
          text,
          textContainer,
          root: classNames(showDropZone ? droparea : hidden)
        }}
        dropzoneText={formatMessage(TXTWCL_Drag_and_drop_a_file_here_or_c_ELL)}
        dropzoneProps={{ onDrop }}
        filesLimit={MAX_FILES}
        // @ts-ignore https://github.com/Yuvaleros/material-ui-dropzone/issues/278
        Icon={() => <DropzoneContent />}
        inputProps={
          {
            id: 'deposit.imageField',
            name: 'deposit.imageField',
            [`data-testid`]: 'payment-images'
          } as DropzoneAreaProps
        }
        maxFileSize={MAX_FILE_SIZE}
        onDelete={onDelete}
        showAlerts={false}
        showPreviews
        showPreviewsInDropzone={false}
        useChipsForPreview
        previewGridClasses={{ container: gridContainer }}
        previewText=""
        previewChipProps={{
          classes: {
            deleteIcon: chipDeleteIcon,
            icon: chipPrimaryIcon,
            label: chipLabel,
            outlined: imageContainer,
            root: preview
          },
          deleteIcon: <Close />,
          icon: <Success />,
          variant: 'outlined'
        }}
      />
      {error && <UploadError error={error} />}
      <ButtonGroup>
        <SubmitButton numberOfAddedFiles={numberOfAddedFiles} setError={setError} />
        <CTAButton
          type="button"
          variant="text"
          onClick={() => setShowPaymentVerificationPrompt(true)}
          translation={TXTWCL_I_canXt_upload_my_file}
        />
      </ButtonGroup>
      {showPaymentVerificationPrompt && (
        <DepositPaymentVerificationPrompt onClose={() => setShowPaymentVerificationPrompt(false)} />
      )}
    </Container>
  );
};

DepositPaymentVerification.displayName = 'DepositPaymentVerification';

export default DepositPaymentVerification;
